import { createWorld, addEntity, addComponent, pipe } from "bitecs";
import React, { useEffect, useRef, useState } from "react";
import { Assets } from "../game/Assets";
import { ModelComponent } from "../game/components/ModelComponent";
import { PositionComponent } from "../game/components/PositionComponent";
import { SoundComponent } from "../game/components/SoundComponent";
import { TagPetComponent } from "../game/components/TagPetComponent";
import { TagWanderComponent } from "../game/components/TagWanderComponent";
import { Sounds } from "../game/Sounds";
import { createAnimationSystem } from "../game/systems/AnimationSystem";
import { createMovementSystem } from "../game/systems/MovementSystem";
import { createPetAnimationSystem } from "../game/systems/PetAnimationSystem";
import { createPetSoundSystem } from "../game/systems/PetSoundSystem";
import { createPetVibrateSystem } from "../game/systems/PetVibrateSystem";
import { createRenderSystem } from "../game/systems/RenderSystem";
import { createRotationSystem } from "../game/systems/RotationSystem";
import { createSoundSystem } from "../game/systems/SoundSystem";
import { timeSystem } from "../game/systems/TimeSystem";
import { createTouchSystem } from "../game/systems/TouchSystem";
import { createWanderSystem } from "../game/systems/WanderSystem";
import { TinyPetsWorld } from "../game/TinyPetsWorld";
import { useLocalStorage } from "../hooks/useLocalstorage";
import "./GamePage.css";
import { PetQuote } from "./PetQuote";

interface GameConfig {
    sound: {
        volume: number;
    };
}

interface GamePageProps {}

export const GamePage: React.FC<GamePageProps> = () => {
    function setupGame(
        mainRef: HTMLCanvasElement,
        config: GameConfig
    ): { onVolumeChange: (volume: number) => void } {
        // set up ECS
        const world = createWorld<TinyPetsWorld>(new TinyPetsWorld());

        // spawn initial entities
        const ground = addEntity(world);

        const pet1 = addEntity(world);
        const pet2 = addEntity(world);
        const pet3 = addEntity(world);

        const backgroundMusic = addEntity(world);

        // add components
        addComponent(world, TagPetComponent, pet1);
        addComponent(world, TagPetComponent, pet2);
        addComponent(world, TagPetComponent, pet3);

        addComponent(world, PositionComponent, ground);
        PositionComponent.x[ground] = 0;
        PositionComponent.y[ground] = 0;
        PositionComponent.z[ground] = 0;

        addComponent(world, PositionComponent, pet1);
        PositionComponent.x[pet1] = 2;
        PositionComponent.y[pet1] = 1.2;
        PositionComponent.z[pet1] = 7.4;

        addComponent(world, PositionComponent, pet2);
        PositionComponent.x[pet2] = 0;
        PositionComponent.y[pet2] = 0.1;
        PositionComponent.z[pet2] = -5;

        addComponent(world, PositionComponent, pet3);
        PositionComponent.x[pet3] = -5.5;
        PositionComponent.y[pet3] = 0.1;
        PositionComponent.z[pet3] = -5.5;

        //addComponent(world, TagWanderComponent, pet1);
        addComponent(world, TagWanderComponent, pet2);
        addComponent(world, TagWanderComponent, pet3);

        addComponent(world, ModelComponent, ground);
        ModelComponent.assetLocation[ground] = Assets.FOREST;
        ModelComponent.scale[ground] = 1;

        addComponent(world, ModelComponent, pet1);
        ModelComponent.assetLocation[pet1] = Assets.CAT_BLACK;
        ModelComponent.scale[pet1] = 5;
        ModelComponent.rotation[pet1].set([0, -3, 0]);

        addComponent(world, ModelComponent, pet2);
        ModelComponent.assetLocation[pet2] = Assets.CAT_WHITE;
        ModelComponent.scale[pet2] = 5;
        ModelComponent.rotation[pet2].set([0, -2.5, 0]);

        addComponent(world, ModelComponent, pet3);
        ModelComponent.assetLocation[pet3] = Assets.CAT_ORANGE;
        ModelComponent.scale[pet3] = 5;
        ModelComponent.rotation[pet3].set([0, -3, 0]);

        addComponent(world, SoundComponent, backgroundMusic);
        // this probably would be easier with https://github.com/NateTheGreatt/bitECS/blob/master/docs/INTRO.md#-component-proxy
        SoundComponent.soundLocation[backgroundMusic].set([Sounds.BACKGROUND]);
        SoundComponent.autoplay[backgroundMusic] = 1;
        SoundComponent.loop[backgroundMusic] = 1;
        SoundComponent.rate[backgroundMusic] = 1;
        SoundComponent.volume[backgroundMusic] = 0.3;

        // create systems
        const { renderer, camera, scene, renderSystem } = createRenderSystem(
            world,
            {
                camera: {
                    fov: 50,
                    aspect: mainRef.clientWidth / mainRef.clientHeight,
                    near: 1,
                    far: 1000,
                    position: { x: -15, y: 10, z: -15 },
                },
                renderer: {
                    height: mainRef.clientHeight,
                    width: mainRef.clientWidth,
                },
            }
        );

        mainRef.append(renderer.domElement);

        const { soundSystem, onVolumeChange } = createSoundSystem(world, {
            globalVolume: config.sound.volume,
        });

        const { petSoundSystem } = createPetSoundSystem(world, {});
        const { animationSystem } = createAnimationSystem(world, {});
        const { petVibrateSystem } = createPetVibrateSystem(world, {});
        const { petAnimationSystem } = createPetAnimationSystem(world, {});
        const { movementSystem } = createMovementSystem(world, {});
        const { wanderSystem } = createWanderSystem(world, {});
        const { rotationSystem } = createRotationSystem(world, camera, {});

        const { touchSystem } = createTouchSystem(
            world,
            renderer,
            camera,
            scene,
            { touchEndDelayMS: 1000, touchOffset: 10, debug: false }
        );

        // start gameloop
        function gameloop() {
            requestAnimationFrame(gameloop);

            // set up pipeline of systems that will be called in order each frame
            const pipeline = pipe(
                timeSystem,
                renderSystem,
                touchSystem,
                petSoundSystem,
                soundSystem,
                petVibrateSystem,
                animationSystem,
                petAnimationSystem,
                movementSystem,
                wanderSystem,
                rotationSystem
            );

            pipeline(world);
        }

        gameloop();

        return { onVolumeChange };
    }

    // link with react
    const mainRef = useRef<HTMLCanvasElement>(null);

    const [running, setRunning] = useState<boolean>(false);

    const [showInfo, setShowInfo] = useState<boolean>(false);

    const [volume, setVolume] = useLocalStorage<number>("volume", 1.0);
    const [onVolumeChangeFunction, setOnVolumeChangeFunction] =
        useState<Function>(() => console.log);

    useEffect(() => {
        if (mainRef && mainRef.current && !running) {
            const { onVolumeChange } = setupGame(mainRef.current, {
                sound: { volume: volume },
            });

            setOnVolumeChangeFunction(() => onVolumeChange);

            setRunning(true);
        }
    }, [mainRef, volume, running]);

    useEffect(() => {
        if (onVolumeChangeFunction) {
            onVolumeChangeFunction(volume);
        }
    }, [volume, onVolumeChangeFunction]);

    return (
        <main ref={mainRef} id="game">
            {showInfo && (
                <div className="info-container">
                    <img
                        style={{ margin: "auto", display: "block" }}
                        alt="relax"
                        src="./relax.svg"
                        height="150"
                    />
                    <PetQuote />
                </div>
            )}
            <div className="ui-container">
                <div className="ui-top">
                    <div className="sound-container">
                        <input
                            className="sound-slider faded-out"
                            type="range"
                            min="1"
                            max="100"
                            value={volume * 100}
                            onChange={(e) => setVolume(+e.target.value / 100)}
                        />
                        <button
                            className="menu-button  faded-out"
                            onClick={(e) => setVolume(0)}
                        >
                            🔇
                        </button>
                    </div>
                </div>

                <div className="ui-bottom">
                    <div className="menu-container">
                        <button
                            className="menu-button faded-out"
                            onClick={(e) => setShowInfo(!showInfo)}
                        >
                            {showInfo ? "Close" : "Quote"}
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
};
